import { translate } from '@teamleader/i18n';
import cx from 'classnames';
import React, { useCallback, useContext, useRef } from 'react';

import InvoiceDetails from './InvoiceDetails';
import InvoiceTabs from './InvoiceTabs';
import { TABS } from './Invoices';
import { useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import useLogoPosition from '../../../../Components/Context/Hooks/useLogoPosition';
import Box from '../../../../Components/UI/Box';
import Branding from '../../../../Components/UI/Branding';
import Button from '../../../../Components/UI/Button';
import ErrorBox from '../../../../Components/UI/ErrorFallback/ErrorBox';
import { REASONS } from '../../../../Components/UI/ErrorFallback/ErrorFallback';
import OverflowingFlexBox from '../../../../Components/UI/OverflowingFlexBox';
import Pdf from '../../../../Components/UI/Pdf';
import { MENUS } from '../../../../Utils/Menus';
import { EVENTS, track } from '../../../../Utils/analytics';
import { canOpenNavigationForInvoice } from '../../../../Utils/canOpenNavigation';
import { assertErrors, isNetworkError } from '../../../../Utils/errors';
import useGlobalContext, { InvoiceContext } from '../../Context';
import { SkeletonPdf } from '../../LoadingSkeleton';
import InvoiceFeedbackMenu from '../../Menu/Invoices/InvoiceFeedbackMenu';
import InvoicePaymentStatusMenu from '../../Menu/Invoices/InvoicePaymentStatusMenu';
import PaymentMethodMenu from '../../Menu/Invoices/PaymentMethodMenu';
import NotFound from '../../NotFound';
import styles from '../../styles.css';

interface InvoicesDetailProps {
  activeTab: TABS;
  handleOpenInvoices: () => void;
  handleOpenCreditNotes: () => void;
  defaultLanguage?: string | null;
  loading: boolean;
  error: Error;
}

const InvoicesDetail: React.FC<InvoicesDetailProps> = ({
  activeTab,
  handleOpenInvoices,
  handleOpenCreditNotes,
  defaultLanguage,
  loading,
  error,
}) => {
  const isSmall = useIsSmall();
  const { pdfs, invoicePdf, openInvoices, paidInvoices, creditedInvoices, invoice, madeTooManyPaymentAttempts } =
    useContext(InvoiceContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { openMenu, paymentServiceProvidersEnabled, handleOpenMenu, type } = useGlobalContext();

  const hasPdfs = pdfs && pdfs.length > 0;
  const payButtonVisible =
    isSmall &&
    !!invoicePdf &&
    paymentServiceProvidersEnabled &&
    invoice &&
    invoice.total.due.amount > 0 &&
    !madeTooManyPaymentAttempts;

  if (!invoice) {
    assertErrors(error);
  }

  const handleRenderPdf = useCallback(() => {
    scrollRef.current!.scrollTop = 0;
  }, []);

  const { rightOfPdf, belowPdf } = useLogoPosition(
    canOpenNavigationForInvoice(openInvoices!.data, paidInvoices!.data, creditedInvoices!.data),
  );

  const handlePay = useCallback(() => {
    handleOpenMenu!(MENUS.PAYMENT);
    track(EVENTS.STARTED_PAYING, { fromHeader: false });
  }, [handleOpenMenu]);

  return (
    <>
      <OverflowingFlexBox ref={scrollRef}>
        {isSmall && (
          <InvoiceTabs
            invoice={invoice}
            fullWidth
            handleOpenInvoices={handleOpenInvoices}
            handleOpenCreditNotes={handleOpenCreditNotes}
            activeTab={activeTab}
          />
        )}
        {isSmall && <InvoiceDetails />}
        <Box className={styles['pdf-holder']}>
          {!hasPdfs && loading && <SkeletonPdf small={isSmall} />}
          {hasPdfs && pdfs.map((pdf, index) => <Pdf key={index} pdf={pdf} onPdfRender={handleRenderPdf} />)}
          {payButtonVisible && (
            <Button className={styles['big-button']} level="primary" onClick={handlePay}>
              {translate('actions.payInvoice')}
            </Button>
          )}
        </Box>
        {rightOfPdf && <Branding className={styles['right-branding']} language={defaultLanguage} type={type} />}
        {belowPdf && (
          <Branding
            className={cx(styles['bottom-branding'], {
              [styles['bottom-branding--mobile']]: payButtonVisible,
            })}
            language={defaultLanguage}
            type={type}
          />
        )}
      </OverflowingFlexBox>
      {!hasPdfs &&
        !loading &&
        (error && isNetworkError(error) ? (
          <Box className={styles['error-box']}>
            <ErrorBox language={defaultLanguage} reason={REASONS.NETWORK} />
          </Box>
        ) : (
          <NotFound />
        ))}
      {openMenu?.menu === MENUS.FEEDBACK && <InvoiceFeedbackMenu />}
      {openMenu?.menu === MENUS.PAYMENT && <PaymentMethodMenu />}
      {openMenu?.menu === MENUS.PAYMENT_STATUS && <InvoicePaymentStatusMenu />}
    </>
  );
};

export default InvoicesDetail;
